import React from "react";
import { Column, Columns, Stack } from "@origin-digital/ods-core";
import { useFormikContext } from "formik";
import { AccountPayer, FormValues } from "./types";
import FormFieldLayout from "./FormComponents/FormFieldLayout";
import ReadOnlyTextField from "./FormComponents/ReadOnlyTextField";
import {
  gasMeterTimeOptions,
  getSiteConditionsLabels,
  hasTotalLoadDetails,
  meterLocationOptions,
  populateTotalLoadSummary,
  pressureOptions,
} from "./helper";
import ReadOnlyFieldWrapper from "./ReadOnlyFieldWrapper";
import { usePlumberName } from "../Hooks/usePlumberName";
import { format } from "date-fns";
import RequestorInfo from "./RequestorInfo";
import ReadOnlyUploadedFile from "./FormComponents/ReadOnlyUploadedFile";
import ReadOnlyContact from "./FormComponents/ReadOnlyContact";

const ReadOnlyGasMeterBlock = () => {
  const { values } = useFormikContext<FormValues>();

  const {
    firstName: licensedPlumberFirstName,
    lastName: licensedPlumberLastName,
    email: licensedPlumberEmail,
    phone: licensedPlumberPhone,
    licenseNumber: licensedPlumberLicenseNumber,
  } = usePlumberName(
    values.gasMeter?.licensedPlumber,
    values.requestingCompany?.msaNumber
  );

  return (
    <Stack
      space={{
        xs: "xxlarge",
        md: "xxxlarge",
      }}
    >
      {/* Form container */}
      <Stack
        space={{
          xs: "xlarge",
          md: "xlarge",
        }}
      >
        {/* Kraken related fields */}
        <ReadOnlyFieldWrapper
          value={values.gasMeter.accountPayer === AccountPayer.COMPANY}
        >
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Account number"
              value={values.requestingCompany?.msaNumber || "Pending"}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <FormFieldLayout>
          <ReadOnlyTextField
            label="Meter Installation Registration Number"
            value={values.gasMeter?.allocateNmiOrderNumber || "Pending"}
          />
        </FormFieldLayout>

        <FormFieldLayout>
          <ReadOnlyTextField
            label="Meter Service Order Number"
            value={values.gasMeter?.meterServiceOrderNumber || "Pending"}
          />
        </FormFieldLayout>
        {/* End Kraken related fields */}
        <ReadOnlyFieldWrapper value={values.premiseAddress?.streetNumber}>
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Street No."
              value={values.premiseAddress?.streetNumber}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>
        <ReadOnlyFieldWrapper value={values.gasMeter?.mirn}>
          <FormFieldLayout>
            <ReadOnlyTextField label="MIRN" value={values.gasMeter?.mirn} />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.gasMeter?.preferDate}>
          <FormFieldLayout>
            <Columns collapseBelow="sm" space={{ xs: "xlarge", md: "medium" }}>
              <Column>
                <ReadOnlyTextField
                  label="Preferred connection date"
                  value={
                    values.gasMeter.preferDate
                      ? format(
                          new Date(values.gasMeter.preferDate),
                          "dd/MM/yyyy"
                        )
                      : ""
                  }
                />
              </Column>
              <Column>
                <ReadOnlyTextField
                  label="Preferred connection time"
                  value={
                    gasMeterTimeOptions.find(
                      (option) => option.value === values.gasMeter?.preferTime
                    )?.label || values.gasMeter?.preferTime
                  }
                />
              </Column>
            </Columns>
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.gasMeter?.cocNumber}>
          <FormFieldLayout>
            <ReadOnlyTextField
              label="CoC or SWN or ESV number"
              value={values.gasMeter?.cocNumber || ""}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.gasMeter?.swnNumber}>
          <FormFieldLayout>
            <ReadOnlyTextField
              label="CoC or SWN or ESV number"
              value={values.gasMeter?.swnNumber || ""}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.gasMeter?.pressure}>
          <FormFieldLayout>
            <Columns collapseBelow="sm" space={{ xs: "xlarge", md: "medium" }}>
              <Column>
                <ReadOnlyTextField
                  label="Pressure"
                  value={
                    pressureOptions.find(
                      (option) => option.value === values.gasMeter?.pressure
                    )?.label || values.gasMeter?.pressure
                  }
                />
              </Column>
              {values.gasMeter?.pressure === "0" ? (
                <Column>
                  <ReadOnlyTextField
                    label="Other pressure value (kPa)"
                    value={values.gasMeter?.otherPressure}
                  />
                </Column>
              ) : null}
            </Columns>
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper
          value={
            values.gasMeter.appliancesChecklistFiles &&
            values.gasMeter.appliancesChecklistFiles.length > 0
          }
        >
          {values.gasMeter.appliancesChecklistFiles?.map((attachment) => (
            <ReadOnlyUploadedFile
              key={attachment.attachmentId}
              attachment={attachment}
            />
          ))}
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.gasMeter?.totalLoad}>
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Total load, MJ/h"
              value={values.gasMeter?.totalLoad}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper
          value={hasTotalLoadDetails(values.gasMeter?.totalLoadDetails)}
        >
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Appliances"
              value={populateTotalLoadSummary(
                values.gasMeter?.totalLoadDetails
              )}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper
          value={values.gasMeter?.siteConditions?.length > 0}
        >
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Site conditions"
              value={getSiteConditionsLabels(values.gasMeter?.siteConditions)}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.gasMeter?.otherSiteCondition}>
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Other site condition"
              value={values.gasMeter?.otherSiteCondition}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.gasMeter?.meterLocation}>
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Meter location"
              value={
                meterLocationOptions.find(
                  (option) => option.value === values.gasMeter?.meterLocation
                )?.label || values.gasMeter?.meterLocation
              }
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.gasMeter?.otherMeterLocation}>
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Other meter location"
              value={values.gasMeter?.otherMeterLocation}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>
        {/* Contacts */}
        <ReadOnlyFieldWrapper
          value={
            values.gasMeter?.licensedPlumber &&
            !values.gasMeter?.homeUserLicencedPlumber
          }
        >
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
            data-id="readOnlyGasMeterBlock.licensedPlumber.column"
          >
            {/* Fields */}
            <Column width="2/3">
              <ReadOnlyContact
                contactType="Licenced plumber"
                firstName={licensedPlumberFirstName}
                lastName={licensedPlumberLastName}
                email={licensedPlumberEmail}
                phone={licensedPlumberPhone}
                licenceNumber={licensedPlumberLicenseNumber}
              />
            </Column>
          </Columns>
        </ReadOnlyFieldWrapper>

        <FormFieldLayout>
          <ReadOnlyTextField
            label="Life support required"
            value={
              values.gasMeter?.lifeSupport === undefined
                ? "N/A"
                : values.gasMeter?.lifeSupport
                ? "Yes"
                : "No"
            }
          />
        </FormFieldLayout>

        <ReadOnlyFieldWrapper value={values.gasMeter?.homeUserLicencedPlumber}>
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
            data-id="readOnlyGasMeterBlock.homeUserLicencedPlumber.column"
          >
            {/* Fields */}
            <Column width="2/3">
              <ReadOnlyContact
                contactType="Licenced plumber"
                firstName={values.gasMeter?.homeUserLicencedPlumber?.firstName}
                lastName={values.gasMeter?.homeUserLicencedPlumber?.lastName}
                email={values.gasMeter?.homeUserLicencedPlumber?.email}
                phone={values.gasMeter?.homeUserLicencedPlumber?.phoneNumber}
                licenceNumber={
                  values.gasMeter?.homeUserLicencedPlumber?.licenseNumber
                }
              />
            </Column>
          </Columns>
        </ReadOnlyFieldWrapper>

        {/* End Contacts */}
        <ReadOnlyFieldWrapper
          value={
            values.gasMeter.sitePlanFiles &&
            values.gasMeter.sitePlanFiles.length > 0
          }
        >
          {values.gasMeter.sitePlanFiles?.map((attachment) => (
            <ReadOnlyUploadedFile
              key={attachment.attachmentId}
              attachment={attachment}
            />
          ))}
        </ReadOnlyFieldWrapper>
        <ReadOnlyFieldWrapper
          value={
            values.gasMeter.shipperDocumentsFiles &&
            values.gasMeter.shipperDocumentsFiles.length > 0
          }
        >
          {values.gasMeter.shipperDocumentsFiles?.map((attachment) => (
            <ReadOnlyUploadedFile
              key={attachment.attachmentId}
              attachment={attachment}
            />
          ))}
        </ReadOnlyFieldWrapper>
        <FormFieldLayout>
          <RequestorInfo
            requesterName={values.gasMeter?.requesterName || ""}
            submittedDateTime={values.gasMeter?.submittedDateTime || ""}
          />
        </FormFieldLayout>
      </Stack>
      {/* End Form container */}
    </Stack>
  );
};

export default ReadOnlyGasMeterBlock;
